import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CreateTaskRequest, Task } from './task.model';

/**
 * Service for managing tasks.
 * Provides methods to list active tasks, create new tasks, and complete existing tasks.
 *
 * @author sadatmalik
 */
@Injectable({ providedIn: 'root' })
export class TaskService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/api/tasks`;

  listActive(activityTypeId: string): Observable<Task[]> {
    const params = new HttpParams()
      .set('activityTypeId', activityTypeId)
      .set('status', 'ACTIVE');
    return this.http.get<Task[]>(this.baseUrl, { params });
  }

  listCompleted(activityTypeId: string): Observable<Task[]> {
    const params = new HttpParams()
      .set('activityTypeId', activityTypeId)
      .set('status', 'COMPLETED');
    return this.http.get<Task[]>(this.baseUrl, { params });
  }

  get(id: string): Observable<Task> {
    return this.http.get<Task>(`${this.baseUrl}/${id}`);
  }

  create(request: CreateTaskRequest): Observable<Task> {
    return this.http.post<Task>(this.baseUrl, request);
  }

  reorder(orderedIds: string[], activityTypeId: string): Observable<Task[]> {
    return this.http.put<Task[]>(`${this.baseUrl}/order`, { orderedIds, activityTypeId });
  }

  moveToActivityType(id: string, activityTypeId: string): Observable<Task> {
    return this.http.patch<Task>(`${this.baseUrl}/${id}/activity-type`, { activityTypeId });
  }

  complete(id: string): Observable<Task> {
    return this.http.patch<Task>(`${this.baseUrl}/${id}/complete`, {});
  }

  focus(id: string): Observable<Task> {
    return this.http.patch<Task>(`${this.baseUrl}/${id}/focus`, {});
  }

  unfocus(id: string): Observable<Task> {
    return this.http.patch<Task>(`${this.baseUrl}/${id}/unfocus`, {});
  }

  prioritize(id: string): Observable<Task> {
    return this.http.patch<Task>(`${this.baseUrl}/${id}/prioritize`, {});
  }

  unprioritize(id: string): Observable<Task> {
    return this.http.patch<Task>(`${this.baseUrl}/${id}/unprioritize`, {});
  }

  reopen(id: string): Observable<Task> {
    return this.http.patch<Task>(`${this.baseUrl}/${id}/reopen`, {});
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  cancelSeries(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}/series`);
  }
}
